<script setup>
import CaretDownIcon from '@iconify-vue/ph/caret-down';
import DotsThreeVerticalIcon from '@iconify-vue/ph/dots-three-vertical';
import {
    formatNumber,
    formatPercentage,
    formatPrice,
} from '@js/utils/formatter';
import {
    CategoryScale,
    Chart as ChartJS,
    Legend,
    LinearScale,
    LineElement,
    PointElement,
    Title,
    Tooltip,
} from 'chart.js';
import { Motion } from 'motion-v';
import {
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuPortal,
    DropdownMenuRoot,
    DropdownMenuTrigger,
} from 'reka-ui';
import { computed, ref } from 'vue';
import { Line } from 'vue-chartjs';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
);

const {
    labels,
    datasets,
    title,
    charts,
    typeAxis1 = 'number',
    withDecimalsAxis1 = true,
    enableAxis2 = false,
    typeAxis2 = 'number',
    withDecimalsAxis2 = true,
    reverseAxis1 = false,
    reverseAxis2 = false,
    axis1BeginAtZero = false,
    minAxis1 = null,
    maxAxis1 = null,
    biggerZeroGridLineAxis2 = false,
    options,
} = defineProps({
    labels: {
        type: Array,
        required: false,
    },
    datasets: {
        type: Array,
        required: true,
    },
    title: {
        type: String,
        required: false,
    },
    charts: {
        type: Object,
        required: false,
    },
    typeAxis1: {
        type: String,
        required: false,
    },
    withDecimalsAxis1: {
        type: Boolean,
        required: false,
    },
    enableAxis2: {
        type: Boolean,
        required: false,
    },
    typeAxis2: {
        type: String,
        required: false,
    },
    withDecimalsAxis2: {
        type: Boolean,
        required: false,
        default: true,
    },
    reverseAxis1: {
        type: Boolean,
        required: false,
        default: false,
    },
    reverseAxis2: {
        type: Boolean,
        required: false,
        default: false,
    },
    axis1BeginAtZero: {
        type: Boolean,
        required: false,
    },
    biggerZeroGridLineAxis2: {
        type: Boolean,
        required: false,
    },
    options: {
        type: Object,
        required: false,
    },
    minAxis1: {
        type: Number,
        required: false,
    },
    maxAxis1: {
        type: Number,
        required: false,
    },
});

const chartHeight = ref(400);

const chartData = computed(() => {
    return {
        labels,
        datasets,
    };
});

const chartOptions = ref({
    maintainAspectRatio: false,
    onResize: (chart, size) => {
        if (size.width < 640) {
            chartHeight.value = 400;
            chart.height = 375;
        } else {
            chartHeight.value = 425;
            chart.height = 400;
        }
    },
    interaction: {
        intersect: false,
        mode: 'index',
    },
    scales: {
        ...(enableAxis2
            ? {
                  y: {
                      position: 'left',
                      reverse: reverseAxis1,
                      ticks: {
                          callback: (value) => {
                              if (typeAxis1 === 'percentage') {
                                  return formatPercentage(
                                      value,
                                      false,
                                      withDecimalsAxis1,
                                  );
                              }

                              if (typeAxis1 === 'price') {
                                  return formatPrice(value, withDecimalsAxis1);
                              }

                              return formatNumber(value, withDecimalsAxis1);
                          },
                      },
                      beginAtZero: axis1BeginAtZero,
                      min: minAxis1,
                      max: maxAxis1,
                  },
                  y1: {
                      position: 'right',
                      reverse: reverseAxis2,
                      grid: {
                          color: (context) => {
                              return context.tick.value === 0 &&
                                  biggerZeroGridLineAxis2
                                  ? '#374151'
                                  : '#e5e7eb';
                          },
                          lineWidth: (context) => {
                              return context.tick.value === 0 &&
                                  biggerZeroGridLineAxis2
                                  ? 2
                                  : 1;
                          },
                      },
                      ticks: {
                          callback: (value) => {
                              if (typeAxis2 === 'percentage') {
                                  return formatPercentage(
                                      value,
                                      false,
                                      withDecimalsAxis2,
                                  );
                              }

                              if (typeAxis2 === 'price') {
                                  return formatPrice(value, withDecimalsAxis2);
                              }

                              return formatNumber(value, withDecimalsAxis2);
                          },
                      },
                  },
              }
            : {
                  y: {
                      reverse: reverseAxis1,
                      ticks: {
                          callback: (value) => {
                              if (typeAxis1 === 'percentage') {
                                  return formatPercentage(
                                      value,
                                      false,
                                      withDecimalsAxis1,
                                  );
                              }

                              if (typeAxis1 === 'price') {
                                  return formatPrice(value, withDecimalsAxis1);
                              }

                              return formatNumber(value, withDecimalsAxis1);
                          },
                      },
                      beginAtZero: axis1BeginAtZero,
                      min: minAxis1,
                      max: maxAxis1,
                  },
              }),
    },
    plugins: {
        tooltip: {
            callbacks: {
                label: (ctx) => {
                    let index = ctx.dataIndex;
                    let label = ctx.dataset.labels[index];
                    let value =
                        typeof ctx.dataset.data[index]['y'] !== 'undefined'
                            ? ctx.dataset.data[index]['y']
                            : ctx.dataset.data[index];
                    let yAxisId = ctx.dataset.yAxisID;

                    let type = typeAxis1;

                    if (yAxisId === 'y1') {
                        type = typeAxis2;
                    }

                    let withDecimals = withDecimalsAxis1;

                    if (yAxisId === 'y1') {
                        withDecimals = withDecimalsAxis2;
                    }

                    if (
                        typeof value === 'object' &&
                        value !== null &&
                        'x' in value &&
                        'y' in value
                    ) {
                        value = value.y;
                    }

                    if (type === 'percentage') {
                        value = formatPercentage(value, false, withDecimals);
                    } else if (type === 'price') {
                        value = formatPrice(value, withDecimals);
                    } else {
                        value = formatNumber(value, withDecimals);
                    }

                    return label + ' | ' + value;
                },
            },
            enabled: false,
            position: 'nearest',
            external: externalTooltipHandler,
        },
    },
});

const getOrCreateTooltip = (chart) => {
    let tooltipEl = document.querySelector(
        `div[data-chart-tooltip="${chart.id}"]`,
    );

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.setAttribute('data-chart-tooltip', chart.id);
        tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
        tooltipEl.style.borderRadius = '3px';
        tooltipEl.style.color = 'white';
        tooltipEl.style.opacity = 1;
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.position = 'fixed';
        tooltipEl.style.transform = 'translate(-50%, 0)';
        tooltipEl.style.transition = 'all .1s ease';
        tooltipEl.style.zIndex = 1000;

        const table = document.createElement('table');
        table.style.margin = '0px';

        tooltipEl.appendChild(table);
        document.body.appendChild(tooltipEl);
    }

    return tooltipEl;
};

function externalTooltipHandler(context) {
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chart);

    if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
    }

    if (tooltip.body) {
        const bodyLines = tooltip.body.map((b) => b.lines);
        const tableRoot = tooltipEl.querySelector('table');
        while (tableRoot.firstChild) tableRoot.firstChild.remove();

        const tbody = document.createElement('tbody');
        let previousGroup = null;

        bodyLines.forEach((body, i) => {
            if (tooltip.dataPoints[i].dataset.group !== previousGroup) {
                previousGroup = tooltip.dataPoints[i].dataset.group;
                const trg = document.createElement('tr');
                const tdg = document.createElement('td');
                tdg.style.whiteSpace = 'nowrap';
                tdg.style.fontWeight = 'bold';
                tdg.style.fontSize = '15px';
                tdg.appendChild(document.createTextNode(previousGroup));
                trg.appendChild(tdg);
                tbody.appendChild(trg);
            }

            const colors = tooltip.labelColors[i];
            const span = document.createElement('span');
            span.style.background = colors.backgroundColor;
            span.style.borderColor = colors.borderColor;
            span.style.borderWidth = '2px';
            span.style.marginRight = '10px';
            span.style.height = '10px';
            span.style.width = '10px';
            span.style.display = 'inline-block';

            const tr = document.createElement('tr');
            const td = document.createElement('td');
            td.style.whiteSpace = 'nowrap';
            td.style.fontSize = '13px';
            td.appendChild(span);
            td.appendChild(document.createTextNode(body));

            tr.appendChild(td);
            tbody.appendChild(tr);
        });

        tableRoot.appendChild(document.createElement('thead'));
        tableRoot.appendChild(tbody);
    }

    const canvasRect = chart.canvas.getBoundingClientRect();
    const { left, right, top, bottom } = chart.chartArea;
    const padding = 8;

    tooltipEl.style.opacity = 1;
    tooltipEl.style.transform = 'translate(-50%, 0)';
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding =
        tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';

    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;

    let centerX = canvasRect.left + tooltip.caretX;

    const offset = 12;
    const minY = canvasRect.top + top + padding;
    const maxY = canvasRect.top + bottom - tooltipHeight - padding;

    let y = canvasRect.top + tooltip.caretY + offset;
    if (y > maxY) {
        y = canvasRect.top + tooltip.caretY - tooltipHeight - offset;
    }
    y = Math.max(minY, Math.min(y, maxY));

    const minCenterX = canvasRect.left + left + padding + tooltipWidth / 2;
    const maxCenterX = canvasRect.left + right - padding - tooltipWidth / 2;
    centerX = Math.max(minCenterX, Math.min(centerX, maxCenterX));

    tooltipEl.style.left = centerX + 'px';
    tooltipEl.style.top = y + 'px';
}

const hideSecondaryAxisPlugin = {
    id: 'hideSecondaryAxis',
    beforeLayout: (chart) => {
        const y1Scale = chart.options.scales.y1;
        if (!y1Scale) return;

        const y1DatasetIndices = chart.data.datasets
            .map((ds, index) => ({ yAxisID: ds.yAxisID, index }))
            .filter((item) => item.yAxisID === 'y1')
            .map((item) => item.index);

        if (y1DatasetIndices.length === 0) return;

        y1Scale.display = y1DatasetIndices.some((index) =>
            chart.isDatasetVisible(index),
        );
    },
};

const cleanupTooltipPlugin = {
    id: 'cleanupTooltip',
    afterDestroy: (chart) => {
        const tooltipEl = document.querySelector(
            `div[data-chart-tooltip="${chart.id}"]`,
        );
        if (tooltipEl) {
            tooltipEl.remove();
        }
    },
};
</script>

<template>
    <div class="rounded-sm border border-gray-200 bg-white">
        <div
            v-if="title || charts || options"
            class="flex h-14 w-full items-center justify-between gap-4 border-b border-gray-200 bg-indigo-100 px-4 py-3"
        >
            <DropdownMenuRoot v-if="charts">
                <DropdownMenuTrigger
                    class="group flex h-[28px] min-w-32 items-center justify-between gap-2 rounded-sm border border-gray-300 px-3 py-1 text-sm ring-1 ring-transparent duration-150 outline-none data-[state=open]:ring-gray-300"
                >
                    <template v-for="(chart, index) in charts" :key="index">
                        <template v-if="chart.active">
                            {{ chart.label }}
                        </template>
                    </template>

                    <CaretDownIcon
                        class="size-4 text-gray-500 duration-150 group-data-[state=open]:rotate-180"
                    />
                </DropdownMenuTrigger>

                <DropdownMenuPortal>
                    <DropdownMenuContent
                        align="start"
                        :side-offset="6"
                        class="z-50"
                    >
                        <Motion
                            :initial="{ opacity: 0, scale: 0.96, y: -4 }"
                            :animate="{ opacity: 1, scale: 1, y: 0 }"
                            :transition="{ duration: 0.15 }"
                            class="flex min-w-32 flex-col overflow-hidden rounded-sm border border-gray-300 bg-white shadow-sm"
                        >
                            <DropdownMenuItem
                                v-for="(chart, index) in charts"
                                :key="index"
                                class="flex h-[28px] cursor-pointer items-center px-3 py-1 text-sm duration-150 outline-none hover:bg-indigo-600 hover:text-white data-[highlighted]:bg-indigo-600 data-[highlighted]:text-white"
                                :class="{
                                    'bg-indigo-600 text-white': chart.active,
                                }"
                                @click="chart.action"
                            >
                                {{ chart.label }}
                            </DropdownMenuItem>
                        </Motion>
                    </DropdownMenuContent>
                </DropdownMenuPortal>
            </DropdownMenuRoot>

            <div
                v-if="title && !charts"
                class="text-xl font-bold text-indigo-600"
            >
                {{ title }}
            </div>

            <DropdownMenuRoot v-if="options">
                <DropdownMenuTrigger class="group max-h-8 outline-none">
                    <DotsThreeVerticalIcon
                        class="size-8 text-indigo-400 duration-200 group-data-[state=open]:text-indigo-600 hover:text-indigo-600"
                    />
                </DropdownMenuTrigger>

                <DropdownMenuPortal>
                    <DropdownMenuContent
                        align="end"
                        :side-offset="6"
                        class="z-50"
                    >
                        <Motion
                            :initial="{ opacity: 0, scale: 0.96, y: -4 }"
                            :animate="{ opacity: 1, scale: 1, y: 0 }"
                            :transition="{ duration: 0.15 }"
                            class="flex min-w-32 flex-col overflow-hidden rounded-sm border border-gray-300 bg-white shadow-sm"
                        >
                            <DropdownMenuItem
                                v-for="(option, index) in options"
                                :key="index"
                                class="flex h-[28px] cursor-pointer items-center px-3 py-1 text-sm whitespace-nowrap duration-150 outline-none hover:bg-indigo-600 hover:text-white data-[highlighted]:bg-indigo-600 data-[highlighted]:text-white"
                                :class="{
                                    'bg-indigo-600 text-white': option.active,
                                }"
                                @click="option.action"
                            >
                                {{ option.label }}
                            </DropdownMenuItem>
                        </Motion>
                    </DropdownMenuContent>
                </DropdownMenuPortal>
            </DropdownMenuRoot>
        </div>

        <div class="px-4 py-3" :style="{ height: chartHeight + 'px' }">
            <Line
                :data="chartData"
                :options="chartOptions"
                :plugins="[hideSecondaryAxisPlugin, cleanupTooltipPlugin]"
            />
        </div>
    </div>
</template>

<style scoped>
:deep(canvas) {
    max-height: 400px;
}
</style>
